// pages/TermsPage.tsx - Enhanced legal terms with translations
import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getPath } from '../config/routes';

const TermsPage: React.FC = () => {
  const { language, t } = useLanguage();
  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-cyan-900 via-blue-900 to-teal-900 py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 text-center">
            {t('terms.title')}
          </h1>
          <p className="text-xl text-cyan-100 max-w-3xl mx-auto text-center">
            {t('terms.subtitle')}
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 via-gray-50/50 to-transparent pointer-events-none" />
      </div>

      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section1.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section1.p1')}
              </p>
              <p className="text-gray-700">
                {t('terms.section1.p2')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section2.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section2.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('terms.section2.item1')}</li>
                <li>{t('terms.section2.item2')}</li>
                <li>{t('terms.section2.item3')}</li>
                <li>{t('terms.section2.item4')}</li>
                <li>{t('terms.section2.item5')}</li>
              </ul>
              <p className="text-gray-700 mt-3">
                {t('terms.section2.p2')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section3.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section3.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('terms.section3.item1')}</li>
                <li>{t('terms.section3.item2')}</li>
                <li>{t('terms.section3.item3')}</li>
                <li>{t('terms.section3.item4')}</li>
                <li>{t('terms.section3.item5')}</li>
                <li>{t('terms.section3.item6')}</li>
              </ul>
              <p className="text-gray-700 mt-3">
                {t('terms.section3.p2')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section4.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section4.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('terms.section4.item1')}</li>
                <li>{t('terms.section4.item2')}</li>
                <li>{t('terms.section4.item3')}</li>
                <li>{t('terms.section4.item4')}</li>
                <li>{t('terms.section4.item5')}</li>
              </ul>
              <p className="text-gray-700 mt-3">
                {t('terms.section4.p2')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section5.title')}</h2>
              <p className="text-gray-700 mb-3">{t('terms.section5.intro')}</p>

              <p className="text-gray-700 mb-3">
                <strong>{t('terms.section5.consumer')}</strong> {t('terms.section5.consumerText')}
              </p>

              <p className="text-gray-700 mb-4">
                <strong>{t('terms.section5.business')}</strong> {t('terms.section5.businessText')}
              </p>

              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('terms.section5.item1')}</li>
                <li>{t('terms.section5.item2')}</li>
                <li>{t('terms.section5.item3')}</li>
                <li>{t('terms.section5.item4')}</li>
                <li>{t('terms.section5.item5')}</li>
              </ul>
              <p className="text-gray-700 mt-3">
                {t('terms.section5.p2')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section6.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section6.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('terms.section6.item1')}</li>
                <li>{t('terms.section6.item2')}</li>
                <li>{t('terms.section6.item3')}</li>
                <li>{t('terms.section6.item4')}</li>
                <li>{t('terms.section6.item5')}</li>
                <li>{t('terms.section6.item6')}</li>
                <li>{t('terms.section6.item7')}</li>
              </ul>
              <p className="text-gray-700 mt-3">
                {t('terms.section6.p2')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section7.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section7.p1')}
              </p>
              <p className="text-gray-700 mb-3">
                {t('terms.section7.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('terms.section7.item1')}</li>
                <li>{t('terms.section7.item2')}</li>
                <li>{t('terms.section7.item3')}</li>
                <li>{t('terms.section7.item4')}</li>
              </ul>
              <p className="text-gray-700 mt-3">
                {t('terms.section7.p2')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section8.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section8.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('terms.section8.item1')}</li>
                <li>{t('terms.section8.item2')}</li>
                <li>{t('terms.section8.item3')}</li>
                <li>{t('terms.section8.item4')}</li>
              </ul>
              <p className="text-gray-700 mt-3">
                {t('terms.section8.p2')}
              </p>
              <p className="text-gray-700 mt-3 font-semibold">
                {t('terms.section8.urgentCancel')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section9.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section9.intro')}
              </p>
              <p className="text-gray-700 mb-3">
                <strong>{t('terms.section9.consumer')}</strong> {t('terms.section9.consumerText')}
              </p>
              <p className="text-gray-700 mb-3">
                <strong>{t('terms.section9.business')}</strong> {t('terms.section9.businessText')}
              </p>
              <p className="text-gray-700 mt-3">
                {t('terms.section9.p2')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section10.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section10.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('terms.section10.item1')}</li>
                <li>{t('terms.section10.item2')}</li>
                <li>{t('terms.section10.item3')}</li>
                <li>{t('terms.section10.item4')}</li>
                <li>{t('terms.section10.item5')}</li>
                <li>{t('terms.section10.item6')}</li>
                <li>{t('terms.section10.item7')}</li>
                <li>{t('terms.section10.item8')}</li>
              </ul>
              <p className="text-gray-700 mt-3">
                {t('terms.section10.p2')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section11.title')}</h2>
              <p className="text-gray-700">
                {t('terms.section11.text')}{' '}
                <a href={getPath('privacy', language)} className="text-blue-600 hover:underline">{t('terms.section11.link')}</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section12.title')}</h2>
              <p className="text-gray-700">
                {t('terms.section12.text')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section13.title')}</h2>
              <p className="text-gray-700 mb-3">
                {t('terms.section13.p1')}
              </p>
              <p className="text-gray-700 mb-3">
                {t('terms.section13.p2')}
              </p>
              <p className="text-gray-700">
                {t('terms.section13.p3')}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">{t('terms.section14.title')}</h2>
              <p className="text-gray-700"><strong>FennoFuture</strong></p>
              <p className="text-gray-700">{t('terms.section14.businessId')}: 3203489-7</p>
              <p className="text-gray-700">{t('terms.section14.address')}: Turuntie 3, 37830 Akaa</p>
              <p className="text-gray-700">{t('terms.section14.email')}: innovation@fennofuture.fi</p>
              <p className="text-gray-700">{t('terms.section14.phone')}: +358 400 416 466</p>
              <p className="text-gray-700 mt-3">{t('terms.section14.customerService')}</p>
            </section>

            <section className="border-t pt-6">
              <p className="text-sm text-gray-600">
                <strong>{t('terms.updated')}:</strong> 4.1.2026
              </p>
              <p className="text-sm text-gray-600 mt-2">
                <strong>{t('terms.version')}:</strong> 2.0
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
